/**
 * IMPORTS
 */
import { useEffect, useState } from "react";

import { Crown, UserCheck, UserMinus, UserPlus, Users, UserX, X } from "lucide-react";

import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@components/sheet/sheet";

import {
  useCandidatosConvite,
  useGerenciarParticipantes,
} from "@/hooks/conversa-cliente/use-participantes-conversa";

import type {
  ICandidatoConvite,
  IEstadoConversa,
  IParticipanteConversa,
} from "@/interface/conversa-cliente";
import { formatarDataHora } from "@/utils/formatar-tempo-relativo";
import { getApiErrorMessage } from "@/utils/normalize-api-error/normalize-api-error";
import { notifyError, notifySuccess } from "@/utils/notify-messages";

const ATRASO_BUSCA_MS = 400;

const ROTULO_TIPO: Record<IParticipanteConversa["tipo"], string> = {
  interno: "Equipe Letscom",
  cliente: "Responsável da empresa",
  subordinado: "Usuário da empresa",
};

const ROTULO_STATUS: Record<IParticipanteConversa["status"], string | null> = {
  aceito: null,
  pendente: "Convite pendente",
  recusado: "Recusou o convite",
  inativo: "Acesso inativado",
};

type PainelParticipantesProps = {
  estado: IEstadoConversa;
};

/**
 * Quem participa da conversa. Os donos (quem iniciou e com quem foi iniciada) convidam pessoas
 * da empresa — o convite chega por notificação e só dá acesso depois de aceito.
 */
function PainelParticipantes({ estado }: PainelParticipantesProps) {
  const [aberto, setAberto] = useState(false);
  const aceitos = estado.participantes.filter((participante) => participante.status === "aceito");
  // Inativos continuam listados (o histórico deles fica), mas vão para o fim.
  const participantesOrdenados = [...estado.participantes].sort(
    (a, b) => Number(a.status === "inativo") - Number(b.status === "inativo")
  );

  return (
    <Sheet open={aberto} onOpenChange={setAberto}>
      <SheetTrigger asChild>
        <button
          type="button"
          className="flex h-10 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-700 hover:bg-gray-50"
        >
          <Users className="size-4" />
          <span className="hidden sm:inline">Participantes</span>
          <span className="rounded-full bg-gray-100 px-1.5 text-xs">{aceitos.length}</span>
        </button>
      </SheetTrigger>
      {/* o X padrão do Sheet (último filho, quase invisível) é trocado pelo botão alinhado ao título */}
      <SheetContent className="flex w-full flex-col gap-4 overflow-y-auto bg-white sm:max-w-md [&>button:last-child]:hidden">
        <SheetHeader>
          <div className="flex items-center justify-between gap-2">
            <SheetTitle>Participantes</SheetTitle>
            <SheetClose
              className="rounded-md p-1.5 text-gray-600 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-custom900"
              aria-label="Fechar participantes"
            >
              <X className="size-5" />
            </SheetClose>
          </div>
          <SheetDescription>
            {estado.pode_convidar
              ? "Convide pessoas da sua empresa. Elas entram na conversa depois de aceitar o convite. Quem já participa pode ter o acesso inativado e reativado depois."
              : "Só quem iniciou a conversa pode convidar novos participantes."}
          </SheetDescription>
        </SheetHeader>

        <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
          {participantesOrdenados.map((participante) => (
            <LinhaParticipante
              key={participante.id}
              participante={participante}
              podeGerenciar={
                estado.pode_convidar &&
                participante.papel === "convidado" &&
                participante.lado === "cliente" &&
                !participante.eh_eu
              }
            />
          ))}
        </ul>

        {estado.pode_convidar && aberto && <ConvidarParticipante />}
      </SheetContent>
    </Sheet>
  );
}

function LinhaParticipante({
  participante,
  podeGerenciar,
}: {
  participante: IParticipanteConversa;
  podeGerenciar: boolean;
}) {
  const { remover, inativar, reativar } = useGerenciarParticipantes();
  const [confirmandoInativacao, setConfirmandoInativacao] = useState(false);
  const status = ROTULO_STATUS[participante.status];
  const inativo = participante.status === "inativo";
  const ocupado = remover.isPending || inativar.isPending || reativar.isPending;
  const nome = participante.pessoa.nome;

  const handleCancelarConvite = () =>
    remover.mutate(participante.id, {
      onSuccess: () => notifySuccess("Convite cancelado."),
      onError: (erro) => notifyError(getApiErrorMessage(erro)),
    });

  const handleInativar = () =>
    inativar.mutate(participante.id, {
      onSuccess: (resposta) => {
        setConfirmandoInativacao(false);
        notifySuccess(resposta?.message || "Acesso do participante inativado.");
      },
      onError: (erro) => notifyError(getApiErrorMessage(erro)),
    });

  const handleReativar = () =>
    reativar.mutate(participante.id, {
      onSuccess: (resposta) =>
        notifySuccess(resposta?.message || "Acesso do participante reativado."),
      onError: (erro) => notifyError(getApiErrorMessage(erro)),
    });

  const podeCancelarConvite =
    podeGerenciar && (participante.status === "pendente" || participante.status === "recusado");
  const podeInativar = podeGerenciar && participante.status === "aceito";
  const podeReativar = podeGerenciar && inativo;

  return (
    <li className={`px-3 py-2.5 ${inativo ? "bg-gray-50" : ""}`}>
      <div className="flex items-center gap-3">
        <div className={`min-w-0 flex-1 ${inativo ? "opacity-60" : ""}`}>
          <p className="flex items-center gap-1.5 truncate text-sm font-medium text-gray-900">
            <span className={`truncate ${inativo ? "line-through decoration-gray-400" : ""}`}>
              {nome}
            </span>
            {participante.eh_eu && <span className="text-xs text-gray-500">(você)</span>}
            {participante.papel === "dono" && (
              <Crown
                className="size-3.5 shrink-0 text-orange-500"
                aria-label="Iniciou a conversa"
              />
            )}
            {inativo && (
              <span className="shrink-0 rounded-full bg-gray-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-600">
                Inativo
              </span>
            )}
          </p>
          <p className="truncate text-xs text-gray-500">
            {ROTULO_TIPO[participante.tipo]}
            {inativo
              ? participante.inativado_em && (
                  <span className="ml-1">
                    · Inativado em{" "}
                    <time dateTime={participante.inativado_em}>
                      {formatarDataHora(participante.inativado_em)}
                    </time>
                  </span>
                )
              : status && <span className="ml-1 text-amber-700">· {status}</span>}
          </p>
        </div>

        {podeCancelarConvite && (
          <button
            type="button"
            onClick={handleCancelarConvite}
            disabled={ocupado}
            aria-label={`Cancelar convite de ${nome}`}
            className="flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-gray-600 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
          >
            <UserMinus className="size-3.5" />
            Cancelar convite
          </button>
        )}
        {podeInativar && !confirmandoInativacao && (
          <button
            type="button"
            onClick={() => setConfirmandoInativacao(true)}
            disabled={ocupado}
            aria-label={`Inativar acesso de ${nome}`}
            className="flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-gray-600 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
          >
            <UserX className="size-3.5" />
            Inativar acesso
          </button>
        )}
        {podeReativar && (
          <button
            type="button"
            onClick={handleReativar}
            disabled={ocupado}
            aria-label={`Reativar acesso de ${nome}`}
            className="flex shrink-0 items-center gap-1 rounded-md border border-orange-200 bg-white px-2 py-1 text-xs font-medium text-orange-700 hover:bg-orange-50 disabled:opacity-50"
          >
            <UserCheck className="size-3.5" />
            {reativar.isPending ? "Reativando..." : "Reativar acesso"}
          </button>
        )}
      </div>

      {podeInativar && confirmandoInativacao && (
        <div
          role="alertdialog"
          aria-label={`Confirmar inativação de ${nome}`}
          className="mt-2 space-y-2 rounded-md border border-red-200 bg-red-50 p-3"
        >
          <p className="text-xs text-red-800">
            <strong>{nome}</strong> perde o acesso à conversa: não poderá ler, enviar mensagens nem
            receber notificações. As mensagens dele continuam no histórico e você pode reativar o
            acesso depois.
          </p>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setConfirmandoInativacao(false)}
              disabled={inativar.isPending}
              className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleInativar}
              disabled={inativar.isPending}
              className="rounded-md bg-red-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
            >
              {inativar.isPending ? "Inativando..." : "Inativar acesso"}
            </button>
          </div>
        </div>
      )}
    </li>
  );
}

function ConvidarParticipante() {
  const [termo, setTermo] = useState("");
  const [busca, setBusca] = useState("");
  const candidatos = useCandidatosConvite(busca, true);
  const { convidar } = useGerenciarParticipantes();

  useEffect(() => {
    const temporizador = setTimeout(() => setBusca(termo.trim()), ATRASO_BUSCA_MS);
    return () => clearTimeout(temporizador);
  }, [termo]);

  const handleConvidar = (candidato: ICandidatoConvite) => {
    const payload =
      candidato.subordinado_id !== null
        ? { subordinado_id: candidato.subordinado_id }
        : { user_id: candidato.user_id as number };
    convidar.mutate(payload, {
      onSuccess: () => notifySuccess(`Convite enviado para ${candidato.nome || candidato.email}.`),
      onError: (erro) => notifyError(getApiErrorMessage(erro)),
    });
  };

  return (
    <section className="space-y-2">
      <h3 className="text-sm font-semibold text-gray-900">Convidar</h3>
      <input
        type="search"
        value={termo}
        onChange={(evento) => setTermo(evento.target.value)}
        placeholder="Buscar por nome ou e-mail..."
        aria-label="Buscar pessoa para convidar"
        className="h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-900 outline-none placeholder:text-gray-400 focus-visible:ring-2 focus-visible:ring-gray-custom900"
      />
      {candidatos.isLoading ? (
        <div className="h-12 animate-pulse rounded-md bg-gray-100" />
      ) : candidatos.isError ? (
        <p className="text-sm text-gray-500">Não foi possível carregar as pessoas.</p>
      ) : (candidatos.data ?? []).length === 0 ? (
        <p className="text-sm text-gray-500">Ninguém disponível para convidar.</p>
      ) : (
        <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
          {(candidatos.data ?? []).map((candidato) => (
            <li
              key={`${candidato.tipo}-${candidato.user_id ?? candidato.subordinado_id}`}
              className="flex items-center gap-3 px-3 py-2"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-gray-900">
                  {candidato.nome || candidato.email}
                </p>
                <p className="truncate text-xs text-gray-500">{candidato.email}</p>
              </div>
              <button
                type="button"
                onClick={() => handleConvidar(candidato)}
                disabled={convidar.isPending}
                className="flex items-center gap-1 rounded-md border border-orange-200 px-2.5 py-1 text-xs font-medium text-orange-700 hover:bg-orange-50 disabled:opacity-50"
              >
                <UserPlus className="size-3.5" />
                Convidar
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * EXPORTS
 */
export { PainelParticipantes };
